import { FieldInputOption } from '../graphql/FormFieldCategory'

// @ts-ignore
export const getIsFirefox = () => {
  return (
    typeof window !== 'undefined' && /firefox/i.test(window.navigator.userAgent)
  )
}

export const fillOptions = (
  fieldLabel: string,
  options: FieldInputOption[]
) => {
  const optionArr: { label: string; value: string; disabled: boolean }[] = [
    { label: fieldLabel, value: fieldLabel, disabled: true }
  ]
  options.forEach((o: FieldInputOption) => {
    optionArr.push({
      label: o.optionLabel,
      value: o.optionValue,
      disabled: false
    })
  })

  return optionArr
}

// List of values that qualify as "empty" according to how our Marketo data is structured
// The | values are specifically for Web_origin__c, which has a | separating two potentially empty/null values
const EMPTY_VALUES = [
  '',
  'null',
  'undefined',
  '|',
  'null|null',
  'null|',
  '|null'
]
// List of fields that we want to keep, even if they're empty
const FIELD_ALLOWLIST = ['craftRestaurantToast', '_mkt_trk']

// Helper Function to delete empty or null entries so they aren't sent to mkto
export const removeEmptyValues = (formData: FormData) => {
  const entries: [string, FormDataEntryValue][] = [
    ...(formData as any).entries()
  ]

  const keptEntries = entries.filter(
    ([key, value]) =>
      FIELD_ALLOWLIST.includes(key) ||
      !(typeof value === 'string' && EMPTY_VALUES.includes(value.trim()))
  )

  if (keptEntries.length === entries.length) {
    return formData
  }

  // FormData.delete removes every entry for a key, so rebuild from the kept
  // entries to preserve other values a repeated field may have
  new Set(entries.map(([key]) => key)).forEach((key) => formData.delete(key))
  keptEntries.forEach(([key, value]) => formData.append(key, value as any))

  return formData
}

// Helper Function to delete unnecessary fields from form data to allow submission to mkto
export const reformatData = (formData: any) => {
  const formId = formData.get('formid')
  const formName = formData.get('mkoProgramName')
  switch (formId) {
    case '1014':
      formData.delete('craftInferredCountry')
      formData.delete('appendVariantTestText')
      formData.delete('craftFormLocation')
      formData.delete('craftJavascriptStatus')
      formData.delete('referringPageURLText')
      formData.delete('Web_initiative__c')
      formData.delete('Web_origin__c')
      formData.delete('Web_medium__c')
      formData.delete('Web_campaign_id__c')
      formData.delete('Web_adset_id__c')
      formData.delete('Web_content__c')
      formData.delete('Web_detail__c')
      formData.delete('Website_Source_Detail__c')
      formData.delete('Last_Blog_Seen__c')
      formData.delete('pageFormSubmissionURL')
      formData.delete('GCLID__c')
      formData.delete('msclkid')
      formData.delete('ChatGPToppref')
      formData.delete('ChatGPTobref')
      formData.delete('ttclid')
      formData.delete('tiktok_ttp')
      formData.delete('postalCode')
    // eslint-disable-next-line no-fallthrough
    case '1008':
    case '1306':
      formData.delete('qQVersion')
      formData.delete('toastType')
      break
    case '1059':
      // This is for the IRR form, which uses almost none of the default data, as it is primarily for our demo forms
      formData.delete('appendVariantTestText')
      formData.delete('appendVariantTestText')
      formData.delete('browserLanguage')
      formData.delete('company')
      formData.delete('cR')
      formData.delete('craftFormLocation')
      formData.delete('craftFormLocation')
      formData.delete('craftInferredCountry')
      formData.delete('craftJavascriptStatus')
      formData.delete('craftJavascriptStatus')
      formData.delete('eI')
      formData.delete('fC')
      formData.delete('gae')
      formData.delete('GCLID__c')
      formData.delete('msclkid')
      formData.delete('ChatGPToppref')
      formData.delete('ChatGPTobref')
      formData.delete('ttclid')
      formData.delete('tiktok_ttp')
      formData.delete('isAjax')
      formData.delete('Last_Blog_Seen__c')
      formData.delete('pageFormSubmissionURL')
      formData.delete('postalCode')
      formData.delete('progressiveEligible')
      formData.delete('qQVersion')
      formData.delete('referringPageURLText')
      formData.delete('referringPageURLText')
      formData.delete('responseTemplate')
      formData.delete('returnURL')
      formData.delete('shownCalendly')
      formData.delete('toastType')
      formData.delete('Web_detail__c')
      formData.delete('Web_initiative__c')
      formData.delete('Web_origin__c')
      formData.delete('Web_medium__c')
      formData.delete('Web_campaign_id__c')
      formData.delete('Web_adset_id__c')
      formData.delete('Web_content__c')
      formData.delete('Website_Source_Detail__c')
      // Remove other fields that we're only using for front-end validation
      // todo - maybe change this, these fields have test values and we might actually want them in the final dataset
      formData.delete('deletionDisclaimer')
      formData.delete('complianceRequestorDisclaimer')
      formData.delete('merchantDisclaimer')
      // In most cases we want the Region value to be used as the Country
      // For EU, we have a separate Country dropdown where users can select that
      // We can't have two dropdowns named Country, so we're updating the data here and removing the Region value
      if (!['EU', 'United Kingdom'].includes(formData.get('region'))) {
        formData.set('country', formData.get('region'))
      }
      formData.delete('region')
      break
    default:
      break
  }

  // Remove empty values from form 1003/'Website-Demo-Interest' and form 1008
  if (
    formId === '1008' ||
    (formId === '1003' && formName === 'Website-Demo-Interest')
  ) {
    formData = removeEmptyValues(formData)
  }

  return formData
}

export const postForm = (
  formData: FormData,
  formActionUrl?: string,
  extraOptions?: RequestInit
) => {
  // It looks like we need this step value to handle multi-step forms on the frontend
  // But the field doesn't exist in Marketo, so it crashes the form submission when present
  // Just delete it and move on
  formData.delete('step')

  formData = reformatData(formData)

  const url = formActionUrl || process.env.GATSBY_FORM_ACTION_URL
  const options: RequestInit = {
    body: formData,
    method: 'POST',
    mode: 'cors', // 'no-cors' for local submits
    ...extraOptions
  }

  return fetch(url, options)
}

export const getFormActionUrl = (formActionURL: string) => {
  let formAction = process.env.GATSBY_FORM_ACTION_URL

  if (
    formActionURL === 'marketo-integration/default/referral' ||
    formActionURL === 'marketo-integration/default/getmarketolead' ||
    formActionURL === 'marketo-integration/default/chilipiper' ||
    formActionURL === 'marketo-integration/default/chilipiperlegacy'
  ) {
    const actionURL = formAction.split('?action=')[0]
    formAction = `${actionURL}?action=${formActionURL}`
  }

  if (
    [
      'contentful-dev.phrasingpro3.com',
      'contentful-staging.phrasingpro3.com',
      'contentful-preview-dev.phrasingpro3.com',
      'contentful-preview-staging.phrasingpro3.com'
    ].includes(window.location.hostname)
  ) {
    if (formActionURL === 'marketo-integration/default/referral') {
      formAction = `?action=${formActionURL}`
    }
    let domain = 'https://staging.phrasingpro3.com'
    if (
      ['contentful-staging.phrasingpro3.com'].includes(window.location.hostname)
    ) {
      domain = `https://${window.location.hostname}/`
    }
    formAction = `${domain}${formAction}`
  }
  if (
    [
      'contentful-preview-master.phrasingpro3.com',
      'contentful-pos-proxy.phrasingpro3.com'
    ].includes(window.location.hostname)
  ) {
    formAction = `https://pos.toasttab.com${formAction}`
  }
  return formAction
}

export const getUtmParams = () => {
  const utmParams: any = {}

  const currentUrl = window.location.href
  let queryString = currentUrl
    ? currentUrl.split('?')[1]
    : window.location.search.slice(1)
  if (queryString) {
    queryString = queryString.split('#')[0]
    const arr = queryString.split('&')
    for (let i = 0; i < arr.length; i++) {
      const a = arr[i].split('=')
      const paramName = a[0]
      const paramValue: any = typeof a[1] === 'undefined' ? true : a[1]
      const qualifiers = [
        'utm_campaign',
        'utm_source',
        'utm_medium',
        'utm_term',
        'utm_id',
        'utm_adset',
        'utm_content',
        'source',
        'gclid',
        'msclkid',
        'oppref',
        'ttclid'
      ]
      if (qualifiers.indexOf(paramName) > -1) {
        utmParams[paramName] = paramValue
      }
    }
  }

  return utmParams
}

export const localizeFormError = (text: string, label: string): string => {
  return text
    .split(/({{fieldLabel}})/)
    .map((chunk) => {
      if (chunk === '{{fieldLabel}}') {
        return label
      }
      return chunk
    })
    .join('')
}

export const getBrowserLanguage = (): string => {
  if (typeof window === 'undefined') {
    return 'en' // Default for SSR
  }

  // Get the browser language (e.g., 'en-US', 'es-ES', 'fr-FR')
  const browserLang = navigator.language || navigator.languages?.[0] || 'en-US'

  // Extract just the language code (e.g., 'en' from 'en-US')
  const langCode = browserLang.split('-')[0].toLowerCase()

  return langCode
}
